/**
 * `#uc2Scaling`: "Scaling opportunity areas", the headline of the Similar watersheds tab.
 *
 * Client round of 3 October 2026 for the content, 7 October 2026 for the shape: one table,
 * one row per candidate, on the column rhythm the tab's other two tables share. Each row
 * carries the four things that decide where a practice would go first: which watershed
 * with positive change it would learn from and how alike the two are, the practice it is
 * furthest behind on and by how many points, whether the rainfall of the two is
 * comparable, and the run's own 0 to 1 score with its verdict in a word.
 *
 * **Nothing here is scored on the client.** `score` and `verdict` are the engine's, read
 * as served; the pane ranks on the score and colours the badge from the verdict.
 *
 * A focused unit no longer narrows the list to one row: its row is highlighted in place,
 * so the reader sees where it stands among the others, and the one sentence under the
 * table explains it. With nothing focused the sentence says how the ranking is made.
 *
 * The table sits in a `ChartFrame`, so it expands into the lightbox like a chart. A run
 * that carries no `scaling_candidates` prints one quiet line.
 */
import { useTranslation } from "react-i18next";

import { ChartFrame } from "../../components/charts/ChartFrame";
import type { IntelUc2ScalingRow } from "../../lib/api";
import { fmtFixed } from "../../lib/format";
import { BarReading } from "./Uc2TableParts";
import { alikePct, pctText } from "./uc2Table";
import {
  UC2_PROGRAMME_SCOPE,
  UC2_SCOPE_ISO3,
  UC2_VERDICT_COLORS,
  clampScore,
  normaliseVerdict,
  rankScaling,
  unitInScope,
} from "./uc2Constants";

export interface Uc2ScalingProps {
  /** `summary.scaling_candidates`, whole: the scope's own slice is taken here. */
  candidates?: Record<string, IntelUc2ScalingRow[]> | undefined;
  /** `'programme'`, or the admin0 code the country level is reading. */
  scope: string;
  /** The focused unit, or `null`: its row is highlighted and explained under the table. */
  focusedId?: string | null;
  /** A practice group's own label, so the row names the group the way the selector does. */
  practiceLabel: (group: string) => string;
  /** How many rows at most (a focused row beyond the cut is still shown, at its rank). */
  limit?: number;
}

/** A candidate belongs to a country scope when it says so, or when its id starts with it. */
function rowInScope(row: IntelUc2ScalingRow, scope: string): boolean {
  if (scope === UC2_PROGRAMME_SCOPE || !scope) return true;
  if (row.country) return row.country === scope;
  return unitInScope(row.unit_id, scope);
}

/** The gap in whole points, or `null` when the run recorded none. */
function gapPoints(gap: number | null | undefined): number | null {
  if (typeof gap !== "number" || !Number.isFinite(gap)) return null;
  return Math.abs(Math.round(gap));
}

export function Uc2Scaling({
  candidates,
  scope,
  focusedId = null,
  practiceLabel,
  limit = 10,
}: Uc2ScalingProps) {
  const { t } = useTranslation();
  /**
   * **A served empty list is not a missing block.** "This run carries nothing yet" and
   * "this scope has nothing left to scale" are different facts, told apart by whether the
   * run served a list at all.
   */
  const served =
    Boolean(candidates) &&
    (candidates![scope] != null || candidates![UC2_PROGRAMME_SCOPE] != null);
  const all = (
    candidates?.[scope] ??
    candidates?.[UC2_PROGRAMME_SCOPE] ??
    []
  ).filter((row) => rowInScope(row, scope));
  const ranked = rankScaling(all);
  const focusedRow = focusedId
    ? ranked.find((row) => row.unit_id === focusedId)
    : undefined;
  const shown = ranked.slice(0, limit);
  const rows =
    focusedRow && !shown.includes(focusedRow) ? [...shown, focusedRow] : shown;
  const na = t("intel.uc2.table.na");

  /** Which of the three rainfall words a row takes. */
  const rainKey = (comparable: boolean | null | undefined) =>
    comparable === true
      ? "rainYes"
      : comparable === false
        ? "rainNo"
        : "rainUnknown";
  const rainClauseKey = (comparable: boolean | null | undefined) =>
    comparable === true ? "yes" : comparable === false ? "no" : "unknown";

  /** The focused row's own sentence, built from the very numbers the row prints. */
  const focusSentence = (row: IntelUc2ScalingRow): string => {
    const verdict = normaliseVerdict(row.verdict);
    const score = clampScore(row.score);
    const points = gapPoints(row.best_gap);
    const rain = t(
      `intel.uc2.scaling.rainClause.${rainClauseKey(row.rain_comparable)}`,
    );
    /**
     * Three cases, not two. A run can serve a reference and **no** practice gap (every
     * group's gap negative or null, so the engine leaves `best_group` null); the missing
     * gap drops only the gap clause and keeps the reference the row names.
     */
    const detail = !row.learn_from
      ? t("intel.uc2.scaling.focus.detailNoLearn", { rain })
      : points != null && row.best_group
        ? t("intel.uc2.scaling.focus.detail", {
            pct: alikePct(row.learn_from.similarity),
            learn: row.learn_from.name,
            points,
            count: points,
            practice: practiceLabel(row.best_group),
            rain,
          })
        : t("intel.uc2.scaling.focus.detailNoGap", {
            pct: alikePct(row.learn_from.similarity),
            learn: row.learn_from.name,
            rain,
          });
    // A row the run could not score says so without printing a placeholder as a number.
    return score != null
      ? t(`intel.uc2.scaling.focus.${verdict}`, {
          name: row.name,
          score: fmtFixed(score, 2),
          detail,
        })
      : t(`intel.uc2.scaling.focus.${verdict}NoScore`, {
          name: row.name,
          detail,
        });
  };

  const gapLabel = t("intel.uc2.table.gap");
  const rainLabel = t("intel.uc2.table.rain");

  const table = (expanded: boolean) => (
    <div className="uc2-tablewrap">
      <table className="uc2-table is-stack uc2-table-scaling">
        <thead>
          <tr>
            <th scope="col" className="c-rank">
              {t("intel.uc2.table.rank")}
            </th>
            <th scope="col" className="c-name">
              {t("intel.uc2.table.watershed")}
            </th>
            <th scope="col" className="c-bar">
              {t("intel.uc2.table.learnFrom")}
            </th>
            <th scope="col" className="c-sub c-gap">
              {gapLabel}
            </th>
            <th scope="col" className="c-sub c-rain">
              {rainLabel}
            </th>
            <th scope="col" className="c-verdict">
              {t("intel.uc2.table.verdict")}
            </th>
            <th scope="col" className="c-score">
              {t("intel.uc2.table.score")}
            </th>
          </tr>
        </thead>
        <tbody>
          {rows.map((row) => {
            const verdict = normaliseVerdict(row.verdict);
            const score = clampScore(row.score);
            const points = gapPoints(row.best_gap);
            const focused = row === focusedRow;
            const scoreText = score != null ? fmtFixed(score, 2) : na;
            return (
              <tr
                key={row.unit_id}
                className={focused ? "is-focus" : undefined}
                aria-current={focused ? "true" : undefined}
                data-testid={
                  expanded ? undefined : `uc2-scaling-row-${row.unit_id}`
                }
              >
                <td className="c-rank uc2-rank" data-testid="uc2-scaling-rank">
                  {ranked.indexOf(row) + 1}
                </td>
                <td className="c-name">
                  <span className="uc2-name uc2-scaling-name">{row.name}</span>
                  {row.country ? (
                    <span className="uc2-meta uc2-scaling-country">
                      {t(
                        `country.${UC2_SCOPE_ISO3[row.country] ?? row.country}`,
                        {
                          defaultValue: row.country,
                        },
                      )}
                    </span>
                  ) : null}
                </td>
                <td className="c-bar" data-testid="uc2-scaling-learn">
                  {row.learn_from ? (
                    <>
                      <span className="uc2-cellname">
                        {row.learn_from.name}
                      </span>
                      <BarReading
                        value={alikePct(row.learn_from.similarity) / 100}
                        text={pctText(alikePct(row.learn_from.similarity))}
                        label={t("intel.uc2.scaling.learnFrom", {
                          name: row.learn_from.name,
                          pct: alikePct(row.learn_from.similarity),
                        })}
                      />
                    </>
                  ) : (
                    <span className="uc2-meta">
                      {t("intel.uc2.scaling.learnFromNone")}
                    </span>
                  )}
                </td>
                <td
                  className="c-sub c-gap"
                  data-label={t("intel.uc2.table.gapInline")}
                >
                  <span data-testid="uc2-scaling-gap">
                    {points != null && row.best_group
                      ? t("intel.uc2.scaling.gap", {
                          practice: practiceLabel(row.best_group),
                          points,
                          count: points,
                        })
                      : t("intel.uc2.scaling.gapNone")}
                  </span>
                </td>
                <td
                  className="c-sub c-rain"
                  data-label={t("intel.uc2.table.rainInline")}
                >
                  <span data-testid="uc2-scaling-rain">
                    {t(`intel.uc2.scaling.${rainKey(row.rain_comparable)}`)}
                  </span>
                </td>
                <td className="c-verdict">
                  <span
                    className="uc2-verdict"
                    data-verdict={verdict}
                    style={{ color: UC2_VERDICT_COLORS[verdict] }}
                  >
                    {t(`intel.uc2.scaling.verdict.${verdict}`)}
                  </span>
                </td>
                <td className="c-score" data-testid="uc2-scaling-score">
                  <BarReading
                    value={score}
                    text={scoreText}
                    color={UC2_VERDICT_COLORS[verdict]}
                    label={t("intel.uc2.scaling.score", { value: scoreText })}
                  />
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );

  return (
    <div id="uc2Scaling" className="result-box" data-testid="uc2-scaling">
      <h5>{t("intel.uc2.scaling.title")}</h5>
      <div className="result-pad">
        {rows.length ? (
          <>
            <ChartFrame
              title={t("intel.uc2.scaling.title")}
              expandLabel={t("intel.uc2.table.expand")}
              testId="uc2-scaling-table"
            >
              {(size) => table(size.expanded)}
            </ChartFrame>
            {focusedRow ? (
              <p className="uc2-table-say" data-testid="uc2-scaling-focus">
                {focusSentence(focusedRow)}
              </p>
            ) : (
              <p className="uc2-table-say" data-testid="uc2-scaling-how">
                {t("intel.uc2.scaling.how")}
                {ranked.length > shown.length ? (
                  <>
                    {" "}
                    <span data-testid="uc2-scaling-truncated">
                      {t("intel.uc2.scaling.truncated", {
                        shown: shown.length,
                        total: ranked.length,
                      })}
                    </span>
                  </>
                ) : null}
              </p>
            )}
          </>
        ) : (
          <p
            className="uc2-quiet"
            data-testid={served ? "uc2-scaling-none" : "uc2-scaling-empty"}
          >
            {t(served ? "intel.uc2.scaling.none" : "intel.uc2.scaling.pending")}
          </p>
        )}
      </div>
    </div>
  );
}

export default Uc2Scaling;
